// components/Carousel.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import "../../css/pages/components/Carousel.css";

export default function CarouselInfinite({
  items = [], // [{ src, alt }]
  autoPlay = true,
  interval = 3500,
  className = "",
}) {
  // 1) Always compute safeItems
  const safeItems = useMemo(
    () => (Array.isArray(items) ? items.filter(Boolean) : []),
    [items],
  );

  const count = safeItems.length;

  // 2) ALL hooks must be declared unconditionally
  const [index, setIndex] = useState(1); // used when count > 1
  const [animate, setAnimate] = useState(true);
  const trackRef = useRef(null);
    const startX = useRef(null);
    const [isPaused, setIsPaused] = useState(false);

const intervalRef = useRef(null);
  // 3) Derived data (safe even if count is 0/1)
  const slides = useMemo(() => {
    if (count <= 1) return safeItems;
    return [safeItems[count - 1], ...safeItems, safeItems[0]];
  }, [safeItems, count]);

  const realIndex = useMemo(() => {
    if (count <= 1) return 0;
    const ri = index - 1;
    if (ri < 0) return count - 1;
    if (ri >= count) return 0;
    return ri;
  }, [index, count]);

  // 4) Autoplay (only when count > 1)
useEffect(() => {
  // only run autoplay when valid
  if (!autoPlay || count <= 1 || isPaused) return;

  // always clear first (prevents multiple intervals)
  if (intervalRef.current) clearInterval(intervalRef.current);

  intervalRef.current = setInterval(() => {
    setIndex((p) => p + 1);
  }, interval);

  return () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };
}, [autoPlay, count, interval, isPaused]);
  
  useEffect(() => {
    const onVisibility = () => {
      if (document.hidden) {
        setIsPaused(true);
      } else {
        // resume + nudge animation so it doesn't stay "stuck"
        setAnimate(false);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setAnimate(true);
            setIsPaused(false);
          });
        });
      }
    };

    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);




  // 5) Infinite jump handling (only when count > 1)
  useEffect(() => {
    if (count <= 1) return;
    const el = trackRef.current;
    if (!el) return;

    const onTransitionEnd = () => {
      if (index === count + 1) {
        setAnimate(false);
        setIndex(1);
        requestAnimationFrame(() =>
          requestAnimationFrame(() => setAnimate(true)),
        );
      }
      if (index === 0) {
        setAnimate(false);
        setIndex(count);
        requestAnimationFrame(() =>
          requestAnimationFrame(() => setAnimate(true)),
        );
      }
    };

    el.addEventListener("transitionend", onTransitionEnd);
    return () => el.removeEventListener("transitionend", onTransitionEnd);
  }, [index, count]);

  // 6) Render cases AFTER hooks
  if (count === 0) return null;

  // Single image (no carousel logic UI)
  if (count === 1) {
    const only = safeItems[0];
    // Carousel view (count > 1)
    return (
      <div
        className={`carousel ${className}`}
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onTouchStart={(e) => {
          setIsPaused(true);
          startX.current = e.touches?.[0]?.clientX ?? null;
        }}
        onTouchEnd={(e) => {
          const endX = e.changedTouches?.[0]?.clientX ?? null;
          if (startX.current == null || endX == null) return;

          const dx = endX - startX.current;
          if (Math.abs(dx) < 40) {
            setIsPaused(false);
            return;
          }

          setIndex((p) => (dx < 0 ? p + 1 : p - 1));
          startX.current = null;
          setIsPaused(false);
        }}
      >
        <div
          ref={trackRef}
          className={`carousel_track ${animate ? "is-animating" : "no-anim"}`}
          style={{ transform: `translateX(-${index * 100}%)` }}
        >
          {slides.map((it, i) => (
            <div className="carousel_slide" key={`${it.src}-${i}`}>
              <img
                className="carousel_img"
                src={it.src}
                alt={it.alt || `Slide ${i + 1}`}
              />
            </div>
          ))}
        </div>

        {/* bottom UI dots only */}
        <div className="carousel_dots">
          {safeItems.map((_, i) => (
            <button
              key={i}
              type="button"
              className={`carousel_dot ${i === realIndex ? "isActive" : ""}`}
              aria-label={`Go to slide ${i + 1}`}
              aria-current={i === realIndex ? "true" : "false"}
              onClick={() => {
                 setIsPaused(true);
                 setAnimate(true);
                 setIndex(i + 1);
                 setTimeout(() => setIsPaused(false), 3500);
              }}
            />
          ))}
        </div>
      </div>
    );
  }

  // Carousel view (count > 1)
  return (
    <div
      className={`carousel ${className}`}
      onTouchStart={(e) => (startX.current = e.touches?.[0]?.clientX ?? null)}
      onTouchEnd={(e) => {
        const endX = e.changedTouches?.[0]?.clientX ?? null;
        if (startX.current == null || endX == null) return;

        const dx = endX - startX.current;
        if (Math.abs(dx) < 40) return;

        setIndex((p) => (dx < 0 ? p + 1 : p - 1));
        startX.current = null;
      }}
    >
      <div
        ref={trackRef}
        className={`carousel_track ${animate ? "is-animating" : "no-anim"}`}
        style={{ transform: `translateX(-${index * 100}%)` }}
      >
        {slides.map((it, i) => (
          <div className="carousel_slide" key={`${it.src}-${i}`}>
            <img
              className="carousel_img"
              src={it.src}
              alt={it.alt || `Slide ${i + 1}`}
            />
          </div>
        ))}
      </div>

      {/* bottom UI dots only */}
      <div className="carousel_dots">
        {safeItems.map((_, i) => (
          <button
            key={i}
            type="button"
            className={`carousel_dot ${i === realIndex ? "isActive" : ""}`}
            aria-label={`Go to slide ${i + 1}`}
            aria-current={i === realIndex ? "true" : "false"}
            onClick={() => {
              setAnimate(true);
              setIndex(i + 1);
            }}
          />
        ))}
      </div>
    </div>
  );
}
